import { clsx } from 'clsx';
import styles from './PaginationControls.module.scss';
import { PaginationData } from './interfaces';
import { getDataIdGenerator } from '../shared/utils/dataId';
import { numFormatter } from '../shared/utils/number/formatter';
import { IconFloatingButton } from '../button/IconFloatingButton/IconFloatingButton';
import { EntityUIState } from '../request/interfaces/entity-ui-state';
import { useAnalyticsTracker } from '../tracking/hooks/useAnalyticsTracker';
import { PaginationTrackers } from './pagination-trackers';

export interface PaginationControlProps {
  dataId: string;
  paginationData: PaginationData;
  listState: EntityUIState;
  nextPage: VoidFunction;
  prevPage: VoidFunction;
  className?: string;
  trackingProps?: object;
}

export function PaginationControls({
  dataId,
  paginationData,
  listState,
  nextPage,
  prevPage,
  className,
  trackingProps = {}
}: PaginationControlProps) {
  const { eventTrack } = useAnalyticsTracker();

  const pageStart = paginationData.page * paginationData.limit + 1;
  const pageEnd = Math.min(
    (paginationData.page + 1) * paginationData.limit,
    paginationData.totalCount
  );

  const canGetPrev = paginationData.page > 0;
  const canGetNext = (paginationData.page + 1) * paginationData.limit < paginationData.totalCount;

  const paginationCountUnknown = listState === EntityUIState.LOADING;

  const disablePrev = !canGetPrev || paginationCountUnknown;

  const disableNext = !canGetNext || paginationCountUnknown;

  const dataIdGenerator = getDataIdGenerator(dataId, 'paginationControl');

  return (
    <div data-id={dataIdGenerator('')} className={`${styles.wrapper} ${className}`}>
      <IconFloatingButton
        dataId={dataIdGenerator('prev')}
        variant='filled'
        onClick={() => {
          eventTrack({
            action: PaginationTrackers.TRACKER_PAGINATION_PREV_CLICK,
            properties: {
              ...{ trackingProps },
              disabled: disablePrev,
              totalCount: paginationData.totalCount,
              page: paginationData.page,
              limit: paginationData.limit
            }
          });

          prevPage();
        }}
        disabled={disablePrev}
        icon='left-arrow'
        size='md'
        className={clsx('prev-btn', styles.controlBtn)}
        iconClasses={styles.controlIcon}
      />

      <span className='mx-2'>
        <b className='text-default text-medium'>
          {`${paginationCountUnknown ? '--' : `${pageStart} - ${pageEnd}`}`}
        </b>
        <span className='text-secondary'>
          {' of '}
          {paginationCountUnknown ? '--' : numFormatter(paginationData.totalCount)}
        </span>
      </span>

      <IconFloatingButton
        dataId={dataIdGenerator('next')}
        variant='filled'
        onClick={() => {
          eventTrack({
            action: PaginationTrackers.TRACKER_PAGINATION_NEXT_CLICK,
            properties: {
              ...{ trackingProps },
              disabled: disablePrev,
              totalCount: paginationData.totalCount,
              page: paginationData.page,
              limit: paginationData.limit
            }
          });

          nextPage();
        }}
        disabled={disableNext}
        icon='right-arrow'
        size='md'
        className={clsx('next-btn', styles.controlBtn)}
        iconClasses={styles.controlIcon}
      />
    </div>
  );
}
